import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RiskGauge, statusMeta, categoryLabel, CATEGORIES } from "@/components/scam";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Phone, ShieldAlert, ShieldCheck, MessageSquarePlus, ThumbsDown, ThumbsUp, Clock, User,
} from "lucide-react";

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "az önce";
  if (s < 3600) return `${Math.floor(s / 60)} dk önce`;
  if (s < 86400) return `${Math.floor(s / 3600)} saat önce`;
  return `${Math.floor(s / 86400)} gün önce`;
}

export default function NumberDetail() {
  const { number } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [vote, setVote] = useState("scam");
  const [category, setCategory] = useState("sahtekarlik");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/numbers/${number}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [number]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data) document.title = `${number} numarası sorgulama - SorguPanel`;
  }, [data, number]);

  const submit = async () => {
    if (comment.trim().length < 3) { toast.error("Lütfen bir yorum yazın"); return; }
    setSubmitting(true);
    try {
      await api.post(`/numbers/${number}/report`, { vote, category, comment });
      toast.success("Yorumunuz başarıyla eklendi");
      setOpen(false);
      setComment("");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-[50vh] grid place-items-center text-slate-400">Yükleniyor...</div>;
  if (!data) return <div className="min-h-[50vh] grid place-items-center text-slate-400">Numara bulunamadı</div>;

  const { stats, reports } = data;
  const meta = statusMeta(stats.status);
  const scamPct = stats.total ? Math.round((stats.scam / stats.total) * 100) : 0;

  const reportBtn = (
    <Button data-testid="open-report-btn" className="bg-blue-600 hover:bg-blue-700" onClick={() => { if (!user) { toast.error("Yorum yapmak için giriş yapın"); nav("/login"); } else setOpen(true); }}>
      <MessageSquarePlus className="h-4 w-4 mr-2" /> Yorum / Rapor Ekle
    </Button>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      {/* Header card */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8 shadow-sm animate-fade-up">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <RiskGauge score={stats.risk_score} status={stats.status} />
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 text-slate-400 mb-1">
              <Phone className="h-4 w-4" /> <span className="text-sm">Telefon Numarası</span>
            </div>
            <h1 className="font-mono font-extrabold text-3xl sm:text-4xl text-slate-900 dark:text-white tracking-tight" data-testid="number-title">{number}</h1>
            <div className={`inline-flex items-center gap-2 mt-3 px-4 py-1.5 rounded-full font-bold text-sm ${meta.bg} ${meta.text}`} data-testid="number-status">
              {stats.status === "safe" ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
              {meta.label}
            </div>
            <p className="mt-3 text-slate-500 text-sm">
              {stats.total > 0
                ? `${stats.total} kullanıcı bu numarayı değerlendirdi.`
                : "Bu numara hakkında henüz rapor yok. İlk yorumu siz yapın."}
            </p>
            <div className="mt-4 flex justify-center md:justify-start">{reportBtn}</div>
          </div>

          {/* Vote breakdown */}
          <div className="w-full md:w-56 space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="inline-flex items-center gap-1 text-red-600 font-medium"><ThumbsDown className="h-3.5 w-3.5" /> Dolandırıcı</span>
                <span className="font-bold">{stats.scam}</span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div className="h-full bg-red-500 rounded-full transition-all" style={{ width: `${scamPct}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><ThumbsUp className="h-3.5 w-3.5" /> Güvenli</span>
                <span className="font-bold">{stats.safe}</span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${100 - scamPct}%` }} />
              </div>
            </div>
            {Object.keys(stats.categories).length > 0 && (
              <div className="pt-2">
                <div className="text-xs text-slate-400 mb-1.5">Şikayet türleri</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(stats.categories).map(([k, v]) => (
                    <span key={k} className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600 text-[11px] font-medium">{categoryLabel(k)} ({v})</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Comments */}
      <div className="mt-8">
        <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white mb-4">Topluluk Yorumları ({reports.length})</h2>
        {reports.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl">
            <MessageSquarePlus className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Henüz yorum yok. İlk raporu ekleyin.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => (
              <div key={r.id} data-testid={`comment-${r.id}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="h-8 w-8 rounded-full bg-blue-600 text-white text-sm font-bold grid place-items-center">{r.user_name?.[0]?.toUpperCase() || "K"}</span>
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-1"><User className="h-3 w-3 text-slate-400" />{r.user_name}</div>
                      <div className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" />{timeAgo(r.created_at)}</div>
                    </div>
                  </div>
                  {r.vote === "scam" ? (
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-50 text-red-600 dark:bg-red-950/40">Dolandırıcı</span>
                  ) : (
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40">Güvenli</span>
                  )}
                </div>
                <div className="text-xs text-blue-600 font-medium mb-1">{categoryLabel(r.category)}</div>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Report dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{number} için yorum ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Değerlendirme</label>
              <div className="grid grid-cols-2 gap-3">
                <button data-testid="vote-scam-btn" onClick={() => setVote("scam")} className={`p-3 rounded-lg border-2 text-sm font-semibold transition-all ${vote === "scam" ? "border-red-500 bg-red-50 text-red-600 dark:bg-red-950/40" : "border-slate-200 dark:border-slate-700 text-slate-500"}`}>
                  <ThumbsDown className="h-4 w-4 mx-auto mb-1" /> Dolandırıcı
                </button>
                <button data-testid="vote-safe-btn" onClick={() => setVote("safe")} className={`p-3 rounded-lg border-2 text-sm font-semibold transition-all ${vote === "safe" ? "border-emerald-500 bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40" : "border-slate-200 dark:border-slate-700 text-slate-500"}`}>
                  <ThumbsUp className="h-4 w-4 mx-auto mb-1" /> Güvenli
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Kategori</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Yorumunuz</label>
              <Textarea data-testid="comment-textarea" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Bu numara hakkındaki deneyiminizi paylaşın..." rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="submit-report-btn" onClick={submit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting ? "Gönderiliyor..." : "Yorumu Gönder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
